// Phase 19 Plan 03 Task 1 — Per-ladder ELO card cell (META-04).
//
// One cell of the 2×3 grid on ProfileScreen. Props are kept minimal so the
// ProfileScreen owner can compose the grid from the 6 ranked LadderKey
// values in DEC-010:
//
//   en: standard_4_en, open_3_en, purist_en
//   sv: standard_4_sv, open_3_sv, purist_sv
//
// Plan 19-01 emits `per_ladder_elos` as an always-populated 8-key map of
// `PerLadderEloEntry { rating, rd, vol } | null` per DEC-010 ladder. The
// basic UI here surfaces only `rating ±rd` per D-019-T3-A — sigma (vol)
// stays on the wire for future operator/debug surfaces but is not
// player-legible. When the caller passes `entry={null}` (this specific
// ladder has no rating row yet) the cell renders an em-dash placeholder
// with the "rating not yet available" aria-label (preserves Phase 15
// Plan 15-08 a11y contract).
//
// Sun-Yellow top-100 border is deferred to a later enrichment plan per
// 15-PATTERNS.md (the server doesn't yet emit a "you are in top 100"
// indicator per-ladder). When that surface lands, the `isTop100` prop here
// will be wired by the caller; the current default is false.

import type { JSX } from 'react'
import { FormattedMessage, useIntl } from 'react-intl'

import type { LadderKey } from '../../net/leaderboard'
import type { PerLadderEloEntry } from '../../net/profile'

export interface LadderEloCardProps {
  readonly ladderKey: LadderKey
  /**
   * Per-ladder Glicko-2 entry from the backend per_ladder_elos projection
   * (Plan 19-01). Null when the player has not played any ranked matches in
   * this ladder.
   */
  readonly entry: PerLadderEloEntry | null
  /**
   * When true, the card draws a Sun-Yellow border (top-100 of this ladder
   * indicator — future enrichment). Default false; not surfaced in v1.1.
   */
  readonly isTop100?: boolean
}

/**
 * Map the LadderKey enum to a copy key consumed via FormattedMessage. Plan 10
 * i18n fills the en/sv values; we register en defaults inline for resilience
 * when a locale catalog hasn't loaded yet.
 */
const LADDER_LABEL_KEY: Record<LadderKey, string> = {
  standard_4_en: 'social.leaderboard.ladder.standard_4l',
  standard_4_sv: 'social.leaderboard.ladder.standard_4l',
  open_3_en: 'social.leaderboard.ladder.open_3l',
  open_3_sv: 'social.leaderboard.ladder.open_3l',
  purist_en: 'social.leaderboard.ladder.purist',
  purist_sv: 'social.leaderboard.ladder.purist',
}

const LADDER_LABEL_DEFAULT: Record<LadderKey, string> = {
  standard_4_en: 'Standard 4L',
  standard_4_sv: 'Standard 4L',
  open_3_en: 'Open 3L',
  open_3_sv: 'Öppen 3L',
  purist_en: 'Purist',
  purist_sv: 'Purist',
}

export function LadderEloCard(props: LadderEloCardProps): JSX.Element {
  const { ladderKey, entry, isTop100 = false } = props
  const intl = useIntl()
  const hasEntry = entry !== null && entry !== undefined

  return (
    <div
      data-testid={`ladder-elo-card-${ladderKey}`}
      data-ladder-key={ladderKey}
      data-has-elo={hasEntry ? 'true' : 'false'}
      style={{
        width: '120px',
        height: '80px',
        padding: 'var(--space-2) var(--space-3)',
        backgroundColor: 'var(--color-card)',
        border: isTop100 ? '3px solid var(--color-cta-primary)' : 'none',
        borderRadius: 'var(--radius-card)',
        boxShadow: 'var(--shadow-card)',
        display: 'flex',
        flexDirection: 'column',
        justifyContent: 'space-between',
        alignItems: 'flex-start',
        fontVariantNumeric: 'tabular-nums',
      }}
    >
      <span
        style={{
          fontFamily: 'var(--font-family-display)',
          fontWeight: 700,
          fontSize: 'var(--font-size-14)',
          color: 'var(--color-text-secondary)',
          textTransform: 'uppercase',
          letterSpacing: '0.04em',
        }}
      >
        <FormattedMessage
          id={LADDER_LABEL_KEY[ladderKey]}
          defaultMessage={LADDER_LABEL_DEFAULT[ladderKey]}
        />
      </span>
      {hasEntry ? (
        // role="img" so the descriptive aria-label is announced by screen
        // readers (biome lint/a11y/useAriaPropsSupportedByRole disallows
        // aria-label on a plain span without a role override). The visual
        // `±` glyph is intentionally NOT in the aria-label — the label
        // resolves to "Rating {rating}, RD {rd}" via the rating_aria key.
        <span
          data-testid={`ladder-elo-value-${ladderKey}`}
          role="img"
          aria-label={intl.formatMessage(
            { id: 'social.profile.elo.rating_aria' },
            { rating: entry.rating, rd: entry.rd },
          )}
          style={{
            fontFamily: 'var(--font-family-display)',
            fontWeight: 700,
            fontSize: 'var(--font-size-22)',
            lineHeight: 1.1,
            color: 'var(--color-player-a)',
            fontVariantNumeric: 'tabular-nums',
          }}
        >
          <span>{entry.rating}</span>
          <span
            style={{
              color: 'var(--color-text-secondary)',
              fontSize: 'var(--font-size-14)',
              marginLeft: 'var(--space-1)',
            }}
          >
            ±{entry.rd}
          </span>
        </span>
      ) : (
        <span
          data-testid={`ladder-elo-value-${ladderKey}`}
          style={{
            fontFamily: 'var(--font-family-display)',
            fontWeight: 700,
            fontSize: 'var(--font-size-22)',
            lineHeight: 1.1,
            color: 'var(--color-player-a)',
            fontVariantNumeric: 'tabular-nums',
          }}
        >
          {/* Em-dash placeholder — rendered when this ladder has no rating
              row yet. Wrap in a role="img" container so the descriptive
              aria-label is announced (biome lint/a11y/useAriaPropsSupportedByRole
              disallows aria-label on a plain span without a role override). */}
          <span
            role="img"
            aria-label={intl.formatMessage({
              id: 'social.profile.elo.rating_unavailable_aria',
            })}
            data-testid={`ladder-elo-dash-${ladderKey}`}
          >
            —
          </span>
        </span>
      )}
    </div>
  )
}
